/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { CaretDownIcon } from '@radix-ui/react-icons'
import * as NavigationMenu from '@radix-ui/react-navigation-menu'
import { useAtomValue } from 'jotai'
import NextLink from 'next/link'
import { usePathname } from 'next/navigation'
import React, { HTMLProps } from 'react'

import { HeaderNavigationBarItem } from '@/components/e-commerce/header/links'
import { navigationItemsAtom } from '@/store/navigation-items'
import { cn } from '@/utils/css'
import { isPathnameCurrent } from '@/utils/links'

/**
 * The navigation menu to be used in the header of the e-commerce website.
 */
const HeaderNavigationBarMenu: React.FC = () => {
  const navigationItems = useAtomValue(navigationItemsAtom)
  return (
    <NavigationMenu.Root>
      <NavigationMenu.List className={'center m-0 flex justify-end list-none rounded-md bg-white p-1 gap-1.5'}>
        {navigationItems?.map((item, index) => (
          <HeaderNavigationBarMenuItem key={index} {...item} />
        ))}
        <NavigationMenu.Indicator
          className={
            'data-[state=visible]:animate-fade-in data-[state=hidden]:animate-fade-out top-full z-10 flex h-2.5 items-end justify-center overflow-hidden transition-[width,transform_250ms_ease]'
          }
        >
          <div className={'relative top-[70%] h-2.5 w-2.5 rotate-45 rounded-tl-[2px] bg-white'} />
        </NavigationMenu.Indicator>
      </NavigationMenu.List>
      <div className={'bg-white absolute top-full left-0 flex w-full justify-center'}>
        <NavigationMenu.Viewport
          className={cn(
            'w-full relative overflow-hidden transition-[width,height] duration-300',
            'bg-white border-b-2 border-primary-500 shadow-lg',
            'py-7 h-[calc(var(--radix-navigation-menu-viewport-height)+3.5rem)]',
          )}
        />
      </div>
    </NavigationMenu.Root>
  )
}

/**
 * A menu item that can be displayed in the navigation menu. It can either be a link or a submenu.
 */
const HeaderNavigationBarMenuItem: React.FC<HeaderNavigationBarItem> = (props) => {
  const { title } = props
  const pathname = usePathname()
  const href = 'href' in props ? props.href : undefined
  const isActive = href ? isPathnameCurrent(pathname, href) : false

  const commonLinkClasses = cn(
    'text-graphite-500 border-b-2 border-transparent px-3 py-2 text-sm font-normal no-underline outline-hidden uppercase',
    'group gap-1 flex select-none items-center justify-between',
    'hover:font-medium hover:border-transparent hover:text-primary-500',
    isActive && 'text-primary-500 border-primary-500',
  )

  if ('submenu' in props) {
    return (
      <NavigationMenu.Item>
        <NavigationMenu.Trigger asChild>
          {href ? (
            <Link href={href} className={commonLinkClasses}>
              {title}
              <CaretDownIcon
                className={
                  'relative top-px transition-transform duration-300 ease-in group-data-[state=open]:-rotate-180'
                }
                aria-hidden
              />
            </Link>
          ) : (
            <div className={commonLinkClasses}>
              {title}
              <CaretDownIcon
                className={
                  'relative top-px transition-transform duration-300 ease-in group-data-[state=open]:-rotate-180'
                }
                aria-hidden
              />
            </div>
          )}
        </NavigationMenu.Trigger>
        <NavigationMenu.Content className={'w-full px-4'}>
          <div className={'flex flex-row w-full'}>{props.submenu}</div>
        </NavigationMenu.Content>
      </NavigationMenu.Item>
    )
  } else {
    return (
      <NavigationMenu.Item>
        <NavigationMenu.Link asChild>
          <Link href={href!} className={cn(commonLinkClasses, 'hover:text-primary-500 hover:border-primary-500')}>
            {title}
          </Link>
        </NavigationMenu.Link>
      </NavigationMenu.Item>
    )
  }
}

/**
 * A link component that can be used in the navigation menu.
 */
const Link: React.FC<HTMLProps<HTMLAnchorElement>> = ({ href, ...props }) => {
  return (
    <NavigationMenu.Link asChild>
      <NextLink href={href!} className={'no-underline'} {...props} />
    </NavigationMenu.Link>
  )
}

export { HeaderNavigationBarMenu }
